/* eslint-disable react-hooks/exhaustive-deps */
import { useCallback, useEffect, useRef, useState } from "react";
import * as c from "../constants";
import { getColour } from "../utils/getColor";
import { buildTransactionLabel } from "../utils/buildTransactionLabel";
import { sunPosAt } from "../utils/sun";
import { useTransactionStream } from "../hooks/useTransactionStream";
import Overlays from "./Overlays";

// Equirectangular projection: the earth texture maps lng/lat linearly to x/y.
const project = (lat: number, lng: number, w: number, h: number) => ({
  x: ((lng + 180) / 360) * w,
  y: ((90 - lat) / 180) * h,
});

export const WorldFlatMap = () => {
  const { transactionCount, arcsData, ringsData, labelData } = useTransactionStream();
  const [dims, setDims] = useState({ w: window.innerWidth, h: window.innerHeight });
  const nightCanvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const onResize = () => setDims({ w: window.innerWidth, h: window.innerHeight });
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  // Day/night terminator: shade the night hemisphere by darkening the day map
  // beneath it (no separate night texture). Drawn on a low-res canvas so the
  // upscaling softens the terminator edge for free.
  useEffect(() => {
    const canvas = nightCanvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    const cw = canvas.width;
    const ch = canvas.height;

    const draw = () => {
      ctx.clearRect(0, 0, cw, ch);
      const [subLng, decDeg] = sunPosAt(new Date());
      let dec = (decDeg * Math.PI) / 180;
      if (Math.abs(dec) < 0.02) dec = (dec < 0 ? -1 : 1) * 0.02; // avoid div-by-0 near equinox

      // Terminator latitude per longitude column
      const N = 180;
      const pts: [number, number][] = [];
      for (let i = 0; i <= N; i++) {
        const lng = -180 + (360 * i) / N;
        const H = ((lng - subLng) * Math.PI) / 180;
        const lat = (Math.atan(-Math.cos(H) / Math.tan(dec)) * 180) / Math.PI;
        pts.push([((lng + 180) / 360) * cw, ((90 - lat) / 180) * ch]);
      }

      // Night region contains the winter pole (bottom when sun is north)
      const darkBottom = dec > 0;
      ctx.save();
      ctx.beginPath();
      ctx.moveTo(pts[0][0], pts[0][1]);
      pts.forEach((p) => ctx.lineTo(p[0], p[1]));
      if (darkBottom) {
        ctx.lineTo(cw, ch);
        ctx.lineTo(0, ch);
      } else {
        ctx.lineTo(cw, 0);
        ctx.lineTo(0, 0);
      }
      ctx.closePath();
      ctx.clip();

      // Darken the day map in the night region with a cool night-blue tint
      ctx.fillStyle = "rgba(6, 16, 40, 0.5)";
      ctx.fillRect(0, 0, cw, ch);
      ctx.restore();
    };

    draw();
    const interval = setInterval(draw, 10000);
    return () => clearInterval(interval);
  }, []);

  // Keep each transaction label fully on-screen: anchor it above its point, then
  // nudge it back inside the viewport if any edge would clip it.
  const clampLabel = useCallback((el: HTMLDivElement | null) => {
    if (!el) return;
    el.style.transform = "translate(-50%, -125%)";
    const r = el.getBoundingClientRect();
    const m = 10;
    let dx = 0;
    let dy = 0;
    if (r.right > window.innerWidth - m) dx = window.innerWidth - m - r.right;
    else if (r.left < m) dx = m - r.left;
    if (r.bottom > window.innerHeight - m) dy = window.innerHeight - m - r.bottom;
    else if (r.top < m) dy = m - r.top;
    if (dx || dy) {
      el.style.transform = `translate(calc(-50% + ${dx}px), calc(-125% + ${dy}px))`;
    }
  }, []);

  const { w, h } = dims;

  return (
    <>
      <Overlays transactionCount={transactionCount} variant="flat" />

      <style>{`
        @keyframes flatArcDraw {
          from { stroke-dashoffset: 1; }
          to { stroke-dashoffset: 0; }
        }
        @keyframes flatRingPulse {
          0% { transform: translate(-50%, -50%) scale(0.2); opacity: 0.9; }
          100% { transform: translate(-50%, -50%) scale(1); opacity: 0; }
        }
        @keyframes flatCloudDrift {
          from { transform: translateX(0); }
          to { transform: translateX(-50%); }
        }
        /* second layer is flipped vertically so its shapes differ from layer 1 */
        @keyframes flatCloudDrift2 {
          from { transform: translateX(0) scaleY(-1); }
          to { transform: translateX(-50%) scaleY(-1); }
        }
        .flat-clouds, .flat-clouds-2 {
          position: absolute;
          top: 0;
          left: 0;
          width: 200%;
          height: 100%;
          z-index: 1;
          pointer-events: none;
          background-image: url('./img/clouds.png');
          background-repeat: repeat-x;
          background-size: 50% 100%;
          /* screen blend: dark/clear-sky areas add nothing, only white clouds show
             (clouds.png's clear areas are semi-opaque, so a plain overlay darkens the map) */
          mix-blend-mode: screen;
        }
        .flat-clouds {
          opacity: 0.45;
          background-position: 0 0;
          animation: flatCloudDrift 140s linear infinite;
        }
        /* different scale-band, vertical offset, flip and speed → looks random, not a single sliding sheet */
        .flat-clouds-2 {
          opacity: 0.32;
          background-position: 27% 40%;
          animation: flatCloudDrift2 88s linear infinite;
        }
        .flat-label {
          position: absolute;
          white-space: nowrap;
          color: var(--light);
          filter: drop-shadow(0 2px 6px rgba(0, 0, 0, 0.6));
        }
        /* Voucher labels are logos, so give them an explicit size on the flat map */
        .flat-label.voucher {
          width: 60px;
          height: 60px;
        }
      `}</style>

      {/* Map base layer */}
      <div style={{
        position: "fixed",
        top: 0,
        left: 0,
        width: "100vw",
        height: "100vh",
        zIndex: 0,
        backgroundColor: "#0b1f2a",
        backgroundImage: "url('./img/world.jpg')",
        backgroundSize: "100% 100%",
        backgroundRepeat: "no-repeat",
      }}>
        {/* Two drifting cloud layers (screen-blended) at different speeds/offsets for a more random look */}
        <div className="flat-clouds" />
        <div className="flat-clouds-2" />

        {/* Day/night terminator + city lights */}
        <canvas
          ref={nightCanvasRef}
          width={720}
          height={360}
          style={{ position: "absolute", inset: 0, width: "100%", height: "100%", zIndex: 2, pointerEvents: "none" }}
        />

        {/* Very light vignette just to frame the edges */}
        <div style={{
          position: "absolute",
          inset: 0,
          zIndex: 2,
          background: "radial-gradient(circle at 50% 45%, rgba(0, 0, 0, 0) 70%, rgba(0, 0, 0, 0.22) 100%)",
        }} />

        {/* Arcs */}
        <svg
          width={w}
          height={h}
          style={{ position: "absolute", top: 0, left: 0, pointerEvents: "none", zIndex: 3 }}
        >
          {arcsData.map((arc, i) => {
            const p1 = project(arc.startLat, arc.startLng, w, h);
            const p2 = project(arc.endLat, arc.endLng, w, h);
            const dist = Math.hypot(p2.x - p1.x, p2.y - p1.y);
            const midX = (p1.x + p2.x) / 2;
            const midY = (p1.y + p2.y) / 2;
            // Bow the curve upward proportional to its length
            const cy = midY - dist * 0.22;
            const color = getColour(arc.type, true);
            return (
              <path
                key={`${arc.startLat}-${arc.startLng}-${arc.endLat}-${arc.endLng}-${i}`}
                d={`M ${p1.x} ${p1.y} Q ${midX} ${cy} ${p2.x} ${p2.y}`}
                fill="none"
                stroke={color}
                strokeWidth={2.5}
                strokeLinecap="round"
                pathLength={1}
                strokeDasharray={1}
                style={{
                  animation: `flatArcDraw ${c.FLIGHT_TIME}ms ease-out forwards`,
                  filter: `drop-shadow(0 0 4px ${color})`,
                }}
              />
            );
          })}
        </svg>

        {/* Landing rings */}
        {ringsData.map((ring, i) => {
          const p = project(ring.lat, ring.lng, w, h);
          return (
            <div
              key={`ring-${ring.lat}-${ring.lng}-${i}`}
              style={{
                position: "absolute",
                left: p.x,
                top: p.y,
                width: 80,
                height: 80,
                marginLeft: -40,
                marginTop: -40,
                borderRadius: "50%",
                border: "2px solid #73f87e",
                pointerEvents: "none",
                zIndex: 3,
                animation: `flatRingPulse ${(c.FLIGHT_TIME * c.ARC_REL_LEN) / c.NUM_RINGS}ms ease-out infinite`,
              }}
            />
          );
        })}

        {/* Transaction labels */}
        <div style={{ position: "absolute", inset: 0, zIndex: 4, pointerEvents: "none" }}>
          {labelData.map((label, i) => {
            const p = project(label.lat, label.lng, w, h);
            const { html, className } = buildTransactionLabel(label);
            return (
              <div
                key={`label-${label.lat}-${label.lng}-${label.amount}-${i}`}
                ref={clampLabel}
                className={`flat-label ${className}`}
                style={{ left: p.x, top: p.y, transform: "translate(-50%, -125%)" }}
                dangerouslySetInnerHTML={{ __html: html }}
              />
            );
          })}
        </div>
      </div>
    </>
  );
};
